import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import {
  Loader2,
  Eye,
  Users,
  FileText,
  Image,
  Sparkles,
  TrendingUp,
  DollarSign,
  CheckCircle2,
  XCircle,
  Activity,
} from "lucide-react";
import { getDashboardSummary } from "@/lib/admin.functions";

export const Route = createFileRoute("/_authenticated/admin/overview")({
  component: Overview,
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <div className="text-center py-20">
        <p className="text-red-400 mb-3">{error.message}</p>
        <button onClick={() => { router.invalidate(); reset(); }} className="bg-white text-zinc-950 px-5 py-2 rounded-full text-sm font-semibold">Try again</button>
      </div>
    );
  },
  notFoundComponent: () => <div className="text-zinc-400">Not found.</div>,
});

type RangeKey = "today" | "yesterday" | "7d" | "30d" | "this_month" | "last_month";

function computeRange(key: RangeKey): { from: string; to: string } {
  const now = new Date();
  const end = new Date(now);
  const start = new Date(now);
  switch (key) {
    case "today":
      start.setHours(0, 0, 0, 0);
      break;
    case "yesterday":
      start.setDate(now.getDate() - 1); start.setHours(0, 0, 0, 0);
      end.setDate(now.getDate() - 1); end.setHours(23, 59, 59, 999);
      break;
    case "7d":
      start.setDate(now.getDate() - 7);
      break;
    case "30d":
      start.setDate(now.getDate() - 30);
      break;
    case "this_month":
      start.setDate(1); start.setHours(0, 0, 0, 0);
      break;
    case "last_month": {
      start.setMonth(now.getMonth() - 1); start.setDate(1); start.setHours(0, 0, 0, 0);
      end.setDate(0); end.setHours(23, 59, 59, 999);
      break;
    }
  }
  return { from: start.toISOString(), to: end.toISOString() };
}

const RANGES: { key: RangeKey; label: string }[] = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "7d", label: "Last 7 days" },
  { key: "30d", label: "Last 30 days" },
  { key: "this_month", label: "This month" },
  { key: "last_month", label: "Last month" },
];

function Overview() {
  const [rangeKey, setRangeKey] = useState<RangeKey>("30d");
  const range = useMemo(() => computeRange(rangeKey), [rangeKey]);
  const fn = useServerFn(getDashboardSummary);
  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ["admin", "summary", rangeKey],
    queryFn: () => fn({ data: range }),
    staleTime: 30_000,
  });

  return (
    <div className="space-y-8 max-w-7xl">
      <header className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="text-amber-300 text-xs font-semibold tracking-widest uppercase">Overview</p>
          <h1 className="font-serif text-3xl lg:text-4xl text-white mt-1">Business Pulse</h1>
          <p className="text-sm text-zinc-500 mt-1">All-up performance across the website, Paint Studio, and lead pipeline.</p>
        </div>
        <div className="flex flex-wrap gap-1.5 bg-white/[0.03] border border-white/10 rounded-full p-1">
          {RANGES.map((r) => (
            <button
              key={r.key}
              onClick={() => setRangeKey(r.key)}
              className={`text-xs px-3 py-1.5 rounded-full transition-colors ${
                rangeKey === r.key ? "bg-amber-300 text-zinc-950 font-semibold" : "text-zinc-400 hover:text-white"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </header>

      {error && <p className="text-red-400 text-sm">{(error as Error).message}</p>}
      {isLoading ? (
        <div className="py-20 grid place-items-center text-zinc-500"><Loader2 className="size-6 animate-spin" /></div>
      ) : data ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
            <Stat icon={Eye} label="Page views" value={data.pageviews.toLocaleString()} />
            <Stat icon={Users} label="Unique visitors" value={data.uniqueVisitors.toLocaleString()} sub={`${data.sessions.toLocaleString()} sessions`} />
            <Stat icon={FileText} label="Estimate requests" value={data.totalEstimates.toLocaleString()} />
            <Stat icon={Image} label="Studio uploads" value={data.studioUploads.toLocaleString()} sub={`${data.completedDesigns} completed`} />
            <Stat icon={Sparkles} label="Photorealistic renders" value={data.renders.toLocaleString()} sub={`${data.segmentations} segmentations`} />
            <Stat icon={TrendingUp} label="Conversion rate" value={`${data.conversionRate.toFixed(2)}%`} sub="sessions → estimate" />
            <Stat icon={DollarSign} label="Avg estimate value" value={`$${Math.round(data.avgEstimateUsd).toLocaleString()}`} />
            <Stat icon={Activity} label="AI cost (credits)" value={data.aiCostCredits.toLocaleString()} sub={data.aiFailures ? `${data.aiFailures} failures` : "all ok"} />
            <Stat icon={CheckCircle2} label="Won jobs" value={data.won.toLocaleString()} tone="ok" />
            <Stat icon={XCircle} label="Lost jobs" value={data.lost.toLocaleString()} tone="bad" />
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <Panel title="Top pages">
              <Bars items={data.topPages.map((p) => ({ label: p.path, value: p.count }))} />
            </Panel>
            <Panel title="Top traffic sources">
              <Bars items={data.topSources.map((p) => ({ label: p.source, value: p.count }))} />
            </Panel>
            <Panel title="Most popular paint colors">
              {data.topColors.length === 0 ? <Empty /> : (
                <ul className="space-y-2">
                  {data.topColors.map((c) => (
                    <li key={c.name} className="flex items-center gap-3 text-sm">
                      <span className="size-6 rounded-md border border-white/15" style={{ backgroundColor: c.hex || "#888" }} />
                      <span className="flex-1 text-zinc-200">{c.name}</span>
                      <span className="text-zinc-500 tabular-nums">{c.count}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Panel>
            <Panel title="Top CTA clicks">
              <Bars items={data.topCTAs.map((p) => ({ label: p.cta, value: p.count }))} />
            </Panel>
            <Panel title="Estimate requests by project type">
              <Bars items={data.projectTypes.map((p) => ({ label: p.type, value: p.count }))} />
            </Panel>
            <Panel title="Pipeline">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <PipeStat label="Won" value={data.won} tone="ok" />
                <PipeStat label="Lost" value={data.lost} tone="bad" />
                <PipeStat label="Total leads" value={data.totalEstimates} />
                <PipeStat label="Win rate" value={data.totalEstimates ? `${Math.round((data.won / data.totalEstimates) * 100)}%` : "—"} />
              </div>
            </Panel>
          </div>
        </>
      ) : null}
      {isFetching && !isLoading && <p className="text-xs text-zinc-400">Refreshing…</p>}
    </div>
  );
}

function Stat({ icon: Icon, label, value, sub, tone }: { icon: React.ComponentType<{ className?: string }>; label: string; value: string; sub?: string; tone?: "ok" | "bad" }) {
  const toneCls = tone === "ok" ? "text-green-300" : tone === "bad" ? "text-red-300" : "text-white";
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur p-4">
      <div className="flex items-center justify-between text-zinc-500">
        <span className="text-[11px] uppercase tracking-wider">{label}</span>
        <Icon className="size-4" />
      </div>
      <p className={`mt-2 font-serif text-2xl tabular-nums ${toneCls}`}>{value}</p>
      {sub && <p className="text-[11px] text-zinc-500 mt-0.5">{sub}</p>}
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
      <h3 className="text-sm font-semibold text-white mb-3">{title}</h3>
      {children}
    </div>
  );
}

function Bars({ items }: { items: { label: string; value: number }[] }) {
  if (!items.length) return <Empty />;
  const max = Math.max(...items.map((i) => i.value), 1);
  return (
    <ul className="space-y-1.5">
      {items.map((i) => (
        <li key={i.label} className="text-xs">
          <div className="flex items-center justify-between gap-3">
            <span className="text-zinc-300 truncate" title={i.label}>{i.label}</span>
            <span className="text-zinc-500 tabular-nums">{i.value}</span>
          </div>
          <div className="h-1.5 mt-1 rounded-full bg-white/5 overflow-hidden">
            <div className="h-full bg-amber-300/70" style={{ width: `${(i.value / max) * 100}%` }} />
          </div>
        </li>
      ))}
    </ul>
  );
}

function PipeStat({ label, value, tone }: { label: string; value: number | string; tone?: "ok" | "bad" }) {
  const t = tone === "ok" ? "text-green-300" : tone === "bad" ? "text-red-300" : "text-white";
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
      <p className="text-[11px] uppercase tracking-wider text-zinc-500">{label}</p>
      <p className={`mt-1 font-serif text-xl tabular-nums ${t}`}>{value}</p>
    </div>
  );
}

function Empty() {
  return <p className="text-xs text-zinc-400 italic">No data in this range yet.</p>;
}